import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const opportunityTypes = [
  { id: 'new_business', name: 'Novo Negócio' },
  { id: 'upgrade', name: 'Upgrade' },
  { id: 'renewal', name: 'Renovação' },
  { id: 'expansion', name: 'Expansão' },
  { id: 'cross_sell', name: 'Cross-Sell' }
];

const opportunityStages = [
  { id: 'qualification', name: 'Qualificação', probability: 20 },
  { id: 'presentation', name: 'Apresentação', probability: 40 },
  { id: 'proposal', name: 'Proposta', probability: 60 },
  { id: 'negotiation', name: 'Negociação', probability: 80 },
  { id: 'closed_won', name: 'Fechado Ganho', probability: 100 },
  { id: 'closed_lost', name: 'Fechado Perdido', probability: 0 }
];

const currencies = [
  { code: 'BRL', name: 'Real Brasileiro (R$)' },
  { code: 'USD', name: 'Dólar Americano (US$)' },
  { code: 'EUR', name: 'Euro (€)' }
];

const Step4Details = () => {
  const navigate = useNavigate();
  const { state, setOpportunityDetails } = useOpportunityWizard();
  
  const [startDate, setStartDate] = useState(state.startDate);
  const [currencyCode, setCurrencyCode] = useState(state.currencyCode);
  const [opportunityType, setOpportunityType] = useState(state.opportunityType || opportunityTypes[0].id);
  const [stage, setStage] = useState(state.stage || 'qualification');
  const [value, setValue] = useState(state.value || 0);
  const [closeProbability, setCloseProbability] = useState(state.closeProbability || 20);
  
  // Redirecionar se nenhum cliente estiver selecionado
  useEffect(() => {
    if (!state.selectedCustomer) {
      navigate('../step1', { relative: 'path' });
    }
  }, [state.selectedCustomer, navigate]);

  const handleNextStep = () => {
    setOpportunityDetails({
      startDate,
      currencyCode,
      opportunityType,
      stage,
      value,
      closeProbability
    });
    
    navigate('../step5', { relative: 'path' });
  };

  const handleStageChange = (newStage: string) => {
    setStage(newStage);
    
    // Atualizar a probabilidade com base no estágio
    const stageObj = opportunityStages.find(s => s.id === newStage);
    if (stageObj) {
      setCloseProbability(stageObj.probability);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <OpportunityWizardLayout
      currentStep={4}
      totalSteps={7}
      title="Detalhes da Oportunidade"
      description="Informe os detalhes da oportunidade"
      onNext={handleNextStep}
      onPrevious={() => navigate('../step3', { relative: 'path' })}
    >
      <div className="space-y-8">
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="font-extrabold mb-2">Informações do cliente</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Cliente:</div>
              <div>{state.selectedCustomer?.name}</div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Data de Início</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="currency-code">Moeda</Label>
              <Select
                value={currencyCode}
                onValueChange={setCurrencyCode}
              >
                <SelectTrigger id="currency-code">
                  <SelectValue placeholder="Selecione a moeda" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="opportunity-type">Tipo de Oportunidade</Label>
              <Select
                value={opportunityType}
                onValueChange={setOpportunityType}
              >
                <SelectTrigger id="opportunity-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {opportunityTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage">Estágio</Label>
              <Select
                value={stage}
                onValueChange={handleStageChange}
              >
                <SelectTrigger id="stage">
                  <SelectValue placeholder="Selecione o estágio" />
                </SelectTrigger>
                <SelectContent>
                  {opportunityStages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name} ({stage.probability}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="close-probability">Probabilidade de Fechamento ({closeProbability}%)</Label>
              <Slider
                id="close-probability"
                min={0}
                max={100}
                step={5}
                value={[closeProbability]}
                onValueChange={(value) => setCloseProbability(value[0])}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="value">Valor da Oportunidade</Label>
              <Input
                id="value"
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
              />
              <div className="text-sm text-muted-foreground mt-1">
                {formatCurrency(value)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </OpportunityWizardLayout>
  );
};

export default Step4Details; 